import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaExternalLinkAlt, FaInstagram, FaTwitter, FaYoutube, FaFacebook, FaLinkedin, FaTiktok, FaSpotify, FaApple } from 'react-icons/fa';
import { useSocialLinks } from '../hooks/useSocialLinks';
import { API_URL } from '../utils/apiUrl';
import { SEOHead } from '../components/SEOHead';
import './LinkHub.css';

// Threads icon component (not available in FA)
const FaThreads = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.88-.73 2.082-1.152 3.48-1.22.966-.047 1.87.039 2.702.243-.06-.655-.267-1.18-.622-1.571-.463-.51-1.193-.776-2.17-.79-1.17.015-2.017.328-2.591.958L7.05 8.185c.89-1.108 2.291-1.69 4.165-1.735 1.544.037 2.755.466 3.6 1.276.838.803 1.286 1.922 1.332 3.327.524.104 1.018.248 1.48.435 1.238.503 2.207 1.3 2.808 2.307.67 1.123.905 2.455.683 3.855-.297 1.87-1.205 3.478-2.699 4.783C16.581 23.54 14.584 24.2 12.186 24zm-.09-10.29c-1.027.05-1.83.335-2.392.849-.502.46-.742 1.034-.713 1.707.026.58.253 1.05.676 1.399.47.387 1.15.587 1.972.587.07 0 .14-.002.21-.006 1.014-.055 1.795-.399 2.322-1.023.45-.532.723-1.263.814-2.178-.858-.26-1.85-.39-2.889-.335z"/>
  </svg>
);

const LinkHub = () => {
  const { slug } = useParams();
  const [hubData, setHubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use centralized social links hook
  const { socialLinks: globalSocialLinks, loading: socialLoading } = useSocialLinks('default');

  useEffect(() => {
    const fetchHubData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/link-hub/pages/${slug}`);
        if (!response.ok) {
          throw new Error('Link hub not found');
        }
        const data = await response.json();
        setHubData(data);
        
        // Apply theme
        applyTheme(data.theme);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchHubData();
    }
  }, [slug]);

  const applyTheme = (theme) => {
    const root = document.documentElement;
    root.style.setProperty('--hub-accent', theme.accent_color || '#004534');
    root.style.setProperty('--hub-accent-bright', theme.accent_bright || '#00d99a');
    
    // Apply custom colors if set
    if (theme.title_color) {
      root.style.setProperty('--hub-text', theme.title_color);
    }
    if (theme.bio_color) {
      root.style.setProperty('--hub-text-secondary', theme.bio_color);
    }
    if (theme.badge_bg_color) {
      root.style.setProperty('--hub-accent-yellow', theme.badge_bg_color);
    }
    
    // Apply theme mode
    if (theme.theme_mode === 'dark') {
      root.setAttribute('data-hub-theme', 'dark');
    } else if (theme.theme_mode === 'light') {
      root.setAttribute('data-hub-theme', 'light');
    } else {
      // Auto mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-hub-theme', prefersDark ? 'dark' : 'light');
    }
  };

  // Build custom inline styles from theme
  const getCustomStyles = () => {
    if (!hubData?.theme) return {};
    const theme = hubData.theme;
    const styles = {};
    
    // Background
    if (theme.background_type === 'gradient' && theme.gradient_start && theme.gradient_end) {
      styles['--custom-bg'] = `linear-gradient(${theme.gradient_angle || 180}deg, ${theme.gradient_start} 0%, ${theme.gradient_end} 100%)`;
    } else if (theme.background_type === 'solid' && theme.background_color) {
      styles['--custom-bg'] = theme.background_color;
    } else if (theme.background_type === 'image' && theme.background_image) {
      styles['--custom-bg'] = `url(${theme.background_image})`;
      styles.backgroundSize = 'cover';
      styles.backgroundPosition = 'center';
    }
    
    // Card
    if (theme.card_background) {
      styles['--custom-card-bg'] = theme.card_background;
    }
    
    // Typography
    if (theme.title_color) {
      styles['--custom-title-color'] = theme.title_color;
    }
    if (theme.link_title_color) {
      styles['--custom-link-title-color'] = theme.link_title_color;
    }
    if (theme.link_subtitle_color) {
      styles['--custom-link-subtitle-color'] = theme.link_subtitle_color;
    }
    
    return styles;
  };

  const handleLinkClick = (link) => {
    // Track click analytics here if needed
    // Placeholder for future click analytics
  };

  // Show loading skeleton while fetching data - never show 404 during load
  if (loading || socialLoading) {
    return (
      <div className="link-hub-page link-hub-loading">
        <div className="link-hub-container">
          <div className="link-hub-header">
            <div className="avatar-wrapper">
              <div className="avatar-skeleton skeleton-pulse"></div>
            </div>
            <div className="header-text-skeleton">
              <div className="title-skeleton skeleton-pulse"></div>
              <div className="bio-skeleton skeleton-pulse"></div>
            </div>
            <div className="social-skeleton">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="social-icon-skeleton skeleton-pulse"></div>
              ))}
            </div>
          </div>
          <div className="links-skeleton">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="link-card-skeleton skeleton-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Only show 404 after loading is complete and there's actually an error or no data
  if (error || !hubData) {
    return (
      <div className="link-hub-error">
        <h1>404</h1>
        <p>Link hub not found</p>
      </div>
    );
  }

  const featuredLinks = hubData.links.filter(link => link.enabled && link.featured);
  const regularLinks = hubData.links.filter(link => link.enabled && !link.featured);
  
  // Filter global social links based on toggle settings
  const defaultToggles = hubData.default_social_toggles || {};
  const filteredGlobalSocialLinks = globalSocialLinks.filter(social => {
    // Get the platform key to check toggle state
    // Global social links have platform property
    const platform = social.platform;
    // Default is enabled (true) unless explicitly set to false
    return defaultToggles[platform] !== false;
  });
  
  // Use hub-specific social links if available, otherwise fall back to filtered global
  const hubSocialLinks = hubData.social_links || [];
  const socialAccounts = hubSocialLinks.length > 0 ? hubSocialLinks : filteredGlobalSocialLinks;
  
  // Get custom styles from theme
  const customStyles = getCustomStyles();
  const showSocialRow = hubData.theme?.show_social_row !== false;
  const cardSpacing = hubData.theme?.card_spacing || 16;

  // Map platform names to icons
  const getSocialIcon = (platform) => {
    const iconMap = {
      instagram: FaInstagram,
      twitter: FaTwitter,
      youtube: FaYoutube,
      facebook: FaFacebook,
      linkedin: FaLinkedin,
      tiktok: FaTiktok,
      spotify: FaSpotify,
      apple: FaApple,
      threads: FaThreads,
      x: FaTwitter
    };
    return iconMap[platform?.toLowerCase()] || FaExternalLinkAlt;
  };

  return (
    <>
      <SEOHead
        title={hubData.title}
        description={hubData.header?.bio || ''}
        path={`/links/${slug}`}
        noindex={!!hubData.noindex}
      />

      <div className={`link-hub-page theme-${hubData.theme.background_style}`} style={customStyles}>
        <div className="link-hub-container">
          
          {/* Header Card */}
          <header className="hub-header">
            <div className="hub-avatar">
              <img src={hubData.header.avatar_image} alt={hubData.header.display_name} />
            </div>
            <h1 className="hub-title">{hubData.header.display_name}</h1>
            <p className="hub-bio">{hubData.header.bio}</p>
          </header>

          {/* Social Icons Row - Now at top, below header */}
          {showSocialRow && socialAccounts.length > 0 && (
            <div className="social-icons-row">
              {socialAccounts.map((social, index) => {
                // Handle both hub-specific and global social links formats
                const Icon = social.icon || getSocialIcon(social.platform);
                const url = social.url || social.link;
                const name = social.name || social.platform;
                
                if (!url) return null;
                
                return (
                  <a
                    key={`${social.platform || social.name || 'social'}-${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label={name}
                    title={name}
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          )}

          {/* Featured Link (if any) */}
          {featuredLinks.length > 0 && (
            <div className="featured-link-section">
              {featuredLinks.map(link => (
                <a
                  key={link.id}
                  href={link.coming_soon ? '#' : link.url}
                  target={link.open_in_new_tab && !link.coming_soon ? '_blank' : '_self'}
                  rel={link.open_in_new_tab ? 'noopener noreferrer' : ''}
                  className={`hub-link featured ${hubData.theme.button_style} ${link.coming_soon ? 'coming-soon' : ''}`}
                  onClick={() => !link.coming_soon && handleLinkClick(link)}
                >
                  <div className="link-content">
                    <span className="link-label">{link.label}</span>
                    {link.subtext && <span className="link-subtext">{link.subtext}</span>}
                    {link.coming_soon && <span className="coming-soon-badge">Coming Soon</span>}
                  </div>
                  {!link.coming_soon && <FaExternalLinkAlt className="link-arrow" />}
                </a>
              ))}
            </div>
          )}

          {/* Regular Links Stack */}
          {regularLinks.length > 0 && (
            <div className="links-stack" style={{ gap: `${cardSpacing}px` }}>
              {regularLinks.map(link => (
                <a
                  key={link.id}
                  href={link.coming_soon ? '#' : link.url}
                  target={link.open_in_new_tab && !link.coming_soon ? '_blank' : '_self'}
                  rel={link.open_in_new_tab ? 'noopener noreferrer' : ''}
                  className={`hub-link ${hubData.theme.button_style} ${link.coming_soon ? 'coming-soon' : ''}`}
                  onClick={() => !link.coming_soon && handleLinkClick(link)}
                >
                  <div className="link-content">
                    <span className="link-label">{link.label}</span>
                    {link.subtext && <span className="link-subtext">{link.subtext}</span>}
                    {link.coming_soon && <span className="coming-soon-badge">Coming Soon</span>}
                  </div>
                  {!link.coming_soon && <FaExternalLinkAlt className="link-arrow" />}
                </a>
              ))}
            </div>
          )}

          {/* Optional Blocks */}
          {hubData.blocks.filter(b => b.enabled).map(block => (
            <div key={block.id} className={`hub-block hub-block-${block.type}`}>
              {/* Render blocks based on type - can be extended */}
              {block.type === 'newsletter_signup' && (
                <div className="newsletter-block">
                  <h3>Stay Updated</h3>
                  <p>Subscribe to our newsletter</p>
                  <form className="newsletter-form">
                    <input type="email" placeholder="Enter your email" />
                    <button type="submit">Subscribe</button>
                  </form>
                </div>
              )}
            </div>
          ))}

          {/* Footer */}
          <footer className="hub-footer">
            <p>Powered by Golf Nuts</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default LinkHub;
